import type { Article } from '../types';
import { DEFAULT_AUTHOR } from '../types';

import addWidgetBody from './add-feedback-widget-to-website.md?raw';
import npsCsatCesBody from './nps-vs-csat-vs-ces.md?raw';
import measureNpsBody from './how-to-measure-nps.md?raw';
import reactNextBody from './collect-feedback-react-nextjs.md?raw';
import hipaaBody from './hipaa-compliant-feedback-collection.md?raw';

export const guides: Article[] = [
  {
    collection: 'guides',
    slug: 'add-feedback-widget-to-website',
    title: 'How to Add a Feedback Widget to Your Website',
    metaTitle: 'How to Add a Feedback Widget to Your Website (2026)',
    description:
      'Add a feedback widget to any website with a single script tag. Step-by-step guide to embedding, customizing, and capturing context — no build step required.',
    eyebrow: 'Guide',
    heroSubtitle:
      'Embed a feedback widget on any site in under five minutes with one script tag — then customize it and capture the context that makes responses useful.',
    body: addWidgetBody,
    author: DEFAULT_AUTHOR,
    datePublished: '2026-06-22',
    dateModified: '2026-06-22',
    readingTime: '6 min read',
    schemaType: 'HowTo',
    howToSteps: [
      { name: 'Add the script tag', text: 'Paste the Pollenate widget script before the closing body tag with your inbox key and API key.' },
      { name: 'Choose a widget type', text: 'Set data-type to emoji, thumbs, stars, nps, csat, or text to match the question you want answered.' },
      { name: 'Customize the look', text: 'Use JSON theme tokens to match your brand colors, copy, and placement.' },
      { name: 'Capture context', text: 'Pass metadata such as plan or version so each response is tagged with the segment it came from.' },
      { name: 'Act on responses', text: 'Listen for the pollenate:submit event or create an automation to route feedback to Slack, email, or a webhook.' },
    ],
    faqs: [
      { question: 'Do I need a build step to add a feedback widget?', answer: 'No. The Pollenate widget is a single script tag that works on static HTML, WordPress, Webflow, Shopify, and any framework with no build configuration.' },
      { question: 'Can I use different widget types on different pages?', answer: 'Yes. You can run emoji, thumbs, stars, NPS, CSAT, or text widgets on different pages from the same inbox by changing the data-type attribute.' },
      { question: 'Will the widget slow down my site?', answer: 'No. The script loads asynchronously with the defer attribute, so it never blocks your page render.' },
    ],
    related: [
      { label: 'NPS vs CSAT vs CES', to: '/guides/nps-vs-csat-vs-ces' },
      { label: 'Collect feedback in React & Next.js', to: '/guides/collect-feedback-react-nextjs' },
      { label: 'Website feedback widget', to: '/use-cases/website-feedback-widget' },
    ],
  },
  {
    collection: 'guides',
    slug: 'nps-vs-csat-vs-ces',
    title: 'NPS vs CSAT vs CES: Which Metric Should You Use?',
    metaTitle: 'NPS vs CSAT vs CES: Which Should You Use? (2026)',
    description:
      'NPS measures loyalty, CSAT measures satisfaction, and CES measures effort. Learn how each is calculated, when to use it, and how to combine all three.',
    eyebrow: 'Guide',
    heroSubtitle:
      'NPS, CSAT, and CES answer three different questions. Here is what each measures, how it is calculated, and when to use it.',
    body: npsCsatCesBody,
    author: DEFAULT_AUTHOR,
    datePublished: '2026-06-22',
    dateModified: '2026-06-22',
    readingTime: '7 min read',
    schemaType: 'Article',
    faqs: [
      { question: 'What is the difference between NPS, CSAT, and CES?', answer: 'NPS measures long-term loyalty and word-of-mouth, CSAT measures satisfaction with a specific experience, and CES measures how much effort a task required.' },
      { question: 'Can I use NPS, CSAT, and CES together?', answer: 'Yes. Mature feedback programs run periodic relationship NPS surveys and add targeted CSAT and CES micro-surveys at key moments in the customer journey.' },
      { question: 'Which metric is best for measuring support quality?', answer: 'CSAT and CES are best for support. CSAT captures satisfaction with the interaction, while CES captures how easy it was to get the issue resolved.' },
    ],
    related: [
      { label: 'How to measure NPS', to: '/guides/how-to-measure-nps' },
      { label: 'Add a feedback widget to your website', to: '/guides/add-feedback-widget-to-website' },
      { label: 'Best NPS tools', to: '/alternatives/best-nps-tools' },
    ],
  },
  {
    collection: 'guides',
    slug: 'how-to-measure-nps',
    title: 'How to Measure NPS (Net Promoter Score)',
    metaTitle: 'How to Measure NPS: Formula & Example (2026)',
    description:
      'Learn how to calculate Net Promoter Score with the exact formula and a worked example, what counts as a good score, and how to collect NPS without survey fatigue.',
    eyebrow: 'Guide',
    heroSubtitle:
      'The NPS formula, a worked example, what a good score looks like, and how to collect responses without hurting your response rate.',
    body: measureNpsBody,
    author: DEFAULT_AUTHOR,
    datePublished: '2026-06-22',
    dateModified: '2026-06-22',
    readingTime: '6 min read',
    schemaType: 'HowTo',
    howToSteps: [
      { name: 'Ask the NPS question', text: 'Ask how likely customers are to recommend you on a 0 to 10 scale, with an open-ended follow-up.' },
      { name: 'Classify responses', text: 'Group respondents into Promoters (9-10), Passives (7-8), and Detractors (0-6).' },
      { name: 'Apply the formula', text: 'Subtract the percentage of Detractors from the percentage of Promoters to get a score from -100 to +100.' },
    ],
    faqs: [
      { question: 'What is the NPS formula?', answer: 'NPS = % Promoters − % Detractors. Promoters score 9-10, Passives score 7-8, and Detractors score 0-6. The result ranges from -100 to +100.' },
      { question: 'What is a good NPS score?', answer: 'Any score above 0 means more Promoters than Detractors. 30-50 is good, 50-70 is excellent, and 70+ is world-class. Always compare against your industry and your own trend.' },
      { question: 'How often should I send NPS surveys?', answer: 'Avoid surveying the same person more than once a quarter. Sample a rotating subset of users and suppress the prompt for anyone who answered in the last 90 days.' },
    ],
    related: [
      { label: 'NPS vs CSAT vs CES', to: '/guides/nps-vs-csat-vs-ces' },
      { label: 'Best NPS tools', to: '/alternatives/best-nps-tools' },
      { label: 'SaaS & product feedback', to: '/use-cases/saas-product-feedback' },
    ],
  },
  {
    collection: 'guides',
    slug: 'collect-feedback-react-nextjs',
    title: 'How to Collect Feedback in React & Next.js',
    metaTitle: 'Collect Feedback in React & Next.js (2026)',
    description:
      'Two ways to collect user feedback in a React or Next.js app: a drop-in script-tag widget or a direct collect API call. Includes App Router and CSP specifics.',
    eyebrow: 'Developer guide',
    heroSubtitle:
      'Add feedback collection to a React or Next.js app with a drop-in widget or a direct API call — including the App Router and CSP details that trip people up.',
    body: reactNextBody,
    author: DEFAULT_AUTHOR,
    datePublished: '2026-06-22',
    dateModified: '2026-06-22',
    readingTime: '7 min read',
    schemaType: 'HowTo',
    howToSteps: [
      { name: 'Add the widget or call the API', text: 'Use the next/script component for a drop-in widget, or POST to the collect endpoint from your own component.' },
      { name: 'Use a collect-scoped key', text: 'Expose only a collect-scoped API key in the browser; keep admin keys server-side.' },
      { name: 'Update context on navigation', text: 'Pass the current route in metadata so feedback stays tagged across client-side navigation.' },
      { name: 'Allow Pollenate in your CSP', text: 'Add pollenate.dev and api.pollenate.dev to your script-src and connect-src directives.' },
    ],
    faqs: [
      { question: 'How do I add a feedback widget in Next.js?', answer: 'Use the next/script component in your root layout with strategy="afterInteractive" and your inbox and API keys as data attributes.' },
      { question: 'Is it safe to put my Pollenate API key in client-side code?', answer: 'Yes, if it is a collect-scoped key. The collect scope can only submit feedback, not read data or change settings, so it is safe to expose like an analytics key.' },
      { question: 'Why does my widget show a "Failed to fetch" error?', answer: 'That almost always means your Content Security Policy is blocking Pollenate. Add pollenate.dev to script-src and api.pollenate.dev to connect-src.' },
    ],
    related: [
      { label: 'Add a feedback widget to your website', to: '/guides/add-feedback-widget-to-website' },
      { label: 'SaaS & product feedback', to: '/use-cases/saas-product-feedback' },
      { label: 'Documentation', to: '/docs' },
    ],
  },
  {
    collection: 'guides',
    slug: 'hipaa-compliant-feedback-collection',
    title: 'HIPAA-Compliant Feedback Collection: A Practical Guide',
    metaTitle: 'HIPAA-Compliant Feedback Collection Guide (2026)',
    description:
      'When does patient feedback fall under HIPAA, what makes a feedback tool compliant, and how to design surveys that protect PHI without hurting response rates.',
    eyebrow: 'Guide',
    heroSubtitle:
      'When patient feedback becomes regulated, what to require from a vendor, and how to design surveys that protect PHI.',
    body: hipaaBody,
    author: DEFAULT_AUTHOR,
    datePublished: '2026-06-22',
    dateModified: '2026-06-22',
    readingTime: '7 min read',
    schemaType: 'Article',
    faqs: [
      { question: 'Does patient feedback fall under HIPAA?', answer: 'Feedback is regulated when collected by or for a covered entity and it contains PHI — health information tied to an identifiable person. Anonymous public ratings usually are not PHI, but open-text comments can capture it unexpectedly.' },
      { question: 'What makes a feedback tool HIPAA-compliant?', answer: 'There is no HIPAA certification. The vendor must sign a BAA and provide encryption in transit and at rest, role-based access controls, audit logging, and data minimization and retention controls.' },
      { question: 'Does Pollenate sign a BAA?', answer: 'Yes. Pollenate Enterprise plans include a signed Business Associate Agreement, field-level encryption options, enforced 2FA, and data residency controls.' },
    ],
    related: [
      { label: 'HIPAA-compliant feedback', to: '/use-cases/hipaa-compliant-feedback' },
      { label: 'Pollenate vs Qualtrics', to: '/vs/qualtrics' },
      { label: 'Pricing', to: '/pricing' },
    ],
  },
];
